import type { FeatureGridBlockPayload } from '@opinix-cloud/shared';

interface VerticalsProps {
  content: FeatureGridBlockPayload;
}

const iconClass = 'h-7 w-7';

/**
 * Map of `iconKey` strings to lucide-style inline SVGs. The CMS only
 * stores the key string; rendering keeps these visuals out of the
 * editable surface so an admin can't accidentally ship broken JSX.
 *
 * Adding a new iconKey: append to this map and document it in the
 * payload contract.
 */
function VerticalIcon({ iconKey }: { iconKey: string | null | undefined }): JSX.Element {
  switch (iconKey) {
    case 'shield':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconClass} aria-hidden="true">
          <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="m9 12 2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'school':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconClass} aria-hidden="true">
          <path d="m2 9 10-5 10 5-10 5L2 9Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M6 11v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M22 9v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'flame':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconClass} aria-hidden="true">
          <path d="M12 2c1 4 4 6 4 10a4 4 0 1 1-8 0c0-2 1-3 1-5s-1-3-1-3 3 1 4-2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      );
    case 'cross':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconClass} aria-hidden="true">
          <path d="M12 4v16M4 12h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
    case 'building':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconClass} aria-hidden="true">
          <path d="M3 7h18v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M9 7V5a3 3 0 1 1 6 0v2" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      );
    case 'globe':
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" className={iconClass} aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
          <path d="M12 3v18M3 12h18M5 7c4 4 10 4 14 0M5 17c4-4 10-4 14 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
  }
}

export function Verticals({ content }: VerticalsProps): JSX.Element {
  const items = content.items ?? [];
  const headingParts = splitHeading(content.heading);
  return (
    <section id="verticals" className="bg-white py-24 sm:py-32">
      <div className="container-x">
        <div className="max-w-2xl">
          {content.eyebrow && <p className="eyebrow">{content.eyebrow}</p>}
          {content.heading && (
            <h2 className="mt-3 section-title">
              {headingParts.before}
              {headingParts.accent && (
                <span className="text-ember-500">{headingParts.accent}</span>
              )}
              {headingParts.after}
            </h2>
          )}
          {content.blurb && (
            <p className="mt-5 text-lg text-steel-600 leading-relaxed">
              {content.blurb}
            </p>
          )}
        </div>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((v) => (
            <div key={v.title} className="card group">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-navy-900 text-cyan-400 transition-colors group-hover:bg-ember-500 group-hover:text-white">
                <VerticalIcon iconKey={v.iconKey} />
              </div>
              <h3 className="mt-5 text-lg font-bold tracking-tight text-navy-900">
                {v.title}
              </h3>
              <p className="mt-2 text-sm text-steel-600 leading-relaxed">
                {v.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * "One platform. Every scenario." — split around the middle word so
 * the ember-500 highlight wraps "Every". The accent is recognized as
 * a substring like "Every" inside the heading; if missing, the
 * heading renders without a highlight.
 */
function splitHeading(
  heading: string | undefined,
): { before: string; accent: string | null; after: string } {
  if (!heading) return { before: '', accent: null, after: '' };
  const accent = 'Every';
  const idx = heading.indexOf(accent);
  if (idx < 0) return { before: heading, accent: null, after: '' };
  return {
    before: heading.slice(0, idx),
    accent,
    after: heading.slice(idx + accent.length),
  };
}
